import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { quotationsApi as Q, inr } from "@/lib/quotations";
import { customersApi } from "@/lib/customers";
import { productsApi } from "@/lib/products";
import { artworkApi } from "@/lib/artwork";
import { crmApi } from "@/lib/crm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";

const EMPTY_ITEM = { product_id: "", product_code: "", item_name: "", size: "", fabric_required: "", description: "", fabric_type: "", gsm: "", width: "", print_type: "", quantity: "", unit: "Meters", rate: "", discount_pct: "", tax_pct: 5 };

function lineTotals(it) {
  const amount = (Number(it.quantity) || 0) * (Number(it.rate) || 0);
  const disc = amount * (Number(it.discount_pct) || 0) / 100;
  const taxable = amount - disc;
  const tax = taxable * (Number(it.tax_pct) || 0) / 100;
  return { amount, taxable, tax, total: taxable + tax };
}

export default function QuotationForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const enquiryParam = sp.get("enquiry");

  const [meta, setMeta] = useState({ units: [], print_types: [], gst_rates: [] });
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    customer_id: "", artwork_id: "", enquiry_id: null, enquiry_no: null,
    items: [{ ...EMPTY_ITEM }], terms: "", validity_date: "", internal_notes: "", customer_notes: "",
  });

  useEffect(() => {
    (async () => {
      const [m, custs] = await Promise.all([Q.meta(), customersApi.list({ status: "Active" })]);
      setMeta(m); setCustomers(custs);
      if (editing) {
        const q = await Q.get(id);
        if (q.status !== "Draft") { toast.error("Only Draft quotations can be edited."); navigate(`/quotations/${id}`); return; }
        setF({
          customer_id: q.customer_id, artwork_id: q.artwork_id || "", enquiry_id: q.enquiry_id, enquiry_no: q.enquiry_no,
          items: (q.items || []).map((i) => ({ ...EMPTY_ITEM, ...i })), terms: q.terms || "",
          validity_date: q.validity_date || "", internal_notes: q.internal_notes || "", customer_notes: q.customer_notes || "",
        });
      } else if (enquiryParam) {
        try {
          const enq = await crmApi.get(enquiryParam);
          const items = (enq.items || []).map((it) => ({
            ...EMPTY_ITEM, fabric_type: it.fabric_type || "", gsm: it.gsm || "", width: it.width || "",
            quantity: it.quantity || "", unit: (it.unit || "meters").toLowerCase() === "kg" ? "Kgs" : (it.unit || "").toLowerCase() === "pcs" ? "Pieces" : "Meters",
            rate: it.target_price || "", description: it.design_ref ? `Design ${it.design_ref}` : "",
          }));
          setF((p) => ({ ...p, customer_id: enq.customer_id || "", enquiry_id: enq.id, enquiry_no: enq.enquiry_no,
            items: items.length ? items : [{ ...EMPTY_ITEM }] }));
          if (!enq.customer_id) toast.info("Select the customer for this enquiry (link the enquiry to a Customer first for auto-fill).");
        } catch (e) { console.error("Enquiry auto-fill failed", e); }
      }
      setLoading(false);
    })();
  }, [id, editing, enquiryParam, navigate]);

  // Load approved+locked artwork for the chosen customer
  useEffect(() => {
    if (!f.customer_id) { setArtworks([]); return; }
    artworkApi.productionReady(f.customer_id).then(setArtworks).catch(() => setArtworks([]));
  }, [f.customer_id]);

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const updItem = (i, k, v) => setF((p) => ({ ...p, items: p.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const pickProduct = (i, pid) => setF((p) => ({
    ...p,
    items: p.items.map((it, idx) => {
      if (idx !== i) return it;
      if (!pid || pid === "none") return { ...it, product_id: "", product_code: "", item_name: "", size: "", fabric_required: "" };
      const prod = products.find((x) => x.id === pid);
      if (!prod) return it;
      const size = prod.width && prod.height ? `${prod.width} × ${prod.height} ${prod.size_unit}` : "";
      return {
        ...it, product_id: prod.id, product_code: prod.item_code, item_name: prod.item_name,
        size, fabric_required: prod.fabric_required || "", description: prod.item_name,
        unit: prod.unit || it.unit, rate: prod.selling_price ?? it.rate, tax_pct: prod.gst_pct ?? it.tax_pct,
      };
    }),
  }));
  const addItem = () => setF((p) => ({ ...p, items: [...p.items, { ...EMPTY_ITEM }] }));
  const removeItem = (i) => setF((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  const selectedCustomer = customers.find((c) => c.id === f.customer_id);
  const taxMode = useMemo(() => {
    const sc = selectedCustomer?.billing_address?.state_code;
    return sc === "24" ? "intra" : "inter";
  }, [selectedCustomer]);

  const totals = useMemo(() => {
    let subtotal = 0, taxable = 0, tax = 0;
    f.items.forEach((it) => { const t = lineTotals(it); subtotal += t.amount; taxable += t.taxable; tax += t.tax; });
    return { subtotal, taxable, tax, grand: taxable + tax };
  }, [f.items]);

  const save = async () => {
    if (!f.customer_id) { toast.error("Select a customer"); return; }
    if (!f.items.some((it) => Number(it.quantity) > 0 && (it.item_name || it.fabric_type))) { toast.error("Add at least one line item with a product (or fabric) and quantity"); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: f.customer_id, artwork_id: f.artwork_id || null,
        enquiry_id: f.enquiry_id || null, enquiry_no: f.enquiry_no || null,
        items: f.items.map((it) => ({ ...it, quantity: Number(it.quantity) || 0, rate: Number(it.rate) || 0,
          fabric_required: Number(it.fabric_required) || 0, product_id: it.product_id || null,
          discount_pct: Number(it.discount_pct) || 0, tax_pct: Number(it.tax_pct) || 0 })),
        terms: f.terms, validity_date: f.validity_date || null, internal_notes: f.internal_notes, customer_notes: f.customer_notes,
      };
      const res = editing ? await Q.update(id, payload) : await Q.create(payload);
      toast.success(editing ? "Quotation updated" : "Quotation created");
      navigate(`/quotations/${res.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6" data-testid="quotation-form-page">
      <button onClick={() => navigate("/quotations")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back</button>
      <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{editing ? "Edit Quotation" : "New Quotation"}</h2>
      {f.enquiry_no && <p className="-mt-3 text-sm text-indigo-600">Converting from enquiry {f.enquiry_no}</p>}

      <Card className="p-6">
        <h3 className="mb-4 font-heading font-semibold text-slate-900">Customer & Artwork</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Customer *</Label>
            <Select value={f.customer_id} onValueChange={(v) => upd("customer_id", v)}>
              <SelectTrigger data-testid="quote-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
            {selectedCustomer && <p className="text-xs text-slate-500">Place of supply: {selectedCustomer.billing_address?.state || "—"} · Tax: {taxMode === "intra" ? "CGST + SGST" : "IGST"}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Artwork (optional — approved & locked only)</Label>
            <Select value={f.artwork_id || "none"} onValueChange={(v) => upd("artwork_id", v === "none" ? "" : v)} disabled={!f.customer_id}>
              <SelectTrigger data-testid="quote-artwork-select"><SelectValue placeholder="No artwork attached" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No artwork (not finalized)</SelectItem>
                {artworks.map((a) => <SelectItem key={a.id} value={a.id}>{a.artwork_code} · {a.name} (V{a.locked_version || a.version})</SelectItem>)}
              </SelectContent>
            </Select>
            {f.customer_id && artworks.length === 0 && <p className="text-xs text-slate-400">No approved & locked artwork for this customer yet.</p>}
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading font-semibold text-slate-900">Line items</h3>
          <Button size="sm" variant="outline" onClick={addItem} data-testid="add-line-item-btn"><Plus className="mr-1 h-3.5 w-3.5" />Add item</Button>
        </div>
        <div className="space-y-4">
          {f.items.map((it, i) => {
            const t = lineTotals(it);
            return (
              <div key={i} className="rounded-md border border-slate-200 p-3" data-testid={`line-item-${i}`}>
                <div className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-6">
                  <div className="md:col-span-3 space-y-1"><Label className="text-xs">Product (auto-fills item, size, fabric, price &amp; GST)</Label>
                    <Select value={it.product_id || "none"} onValueChange={(v) => pickProduct(i, v)}>
                      <SelectTrigger data-testid={`item-product-${i}`}><SelectValue placeholder="Select product (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No product (manual entry)</SelectItem>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.item_code} · {p.item_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2 space-y-1"><Label className="text-xs">Item name</Label><Input value={it.item_name} onChange={(e) => updItem(i, "item_name", e.target.value)} data-testid={`item-name-${i}`} placeholder="From product or manual" /></div>
                  <div className="space-y-1"><Label className="text-xs">Size</Label><Input value={it.size} onChange={(e) => updItem(i, "size", e.target.value)} data-testid={`item-size-${i}`} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
                  <div className="col-span-2 md:col-span-2 space-y-1"><Label className="text-xs">Fabric</Label><Input value={it.fabric_type} onChange={(e) => updItem(i, "fabric_type", e.target.value)} data-testid={`item-fabric-${i}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">GSM</Label><Input value={it.gsm} onChange={(e) => updItem(i, "gsm", e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">Fabric req (m/unit)</Label><Input type="number" value={it.fabric_required} onChange={(e) => updItem(i, "fabric_required", e.target.value)} data-testid={`item-fabricreq-${i}`} /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Print type</Label>
                    <Select value={it.print_type || ""} onValueChange={(v) => updItem(i, "print_type", v)}>
                      <SelectTrigger data-testid={`item-printtype-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{meta.print_types.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-6">
                  <div className="space-y-1"><Label className="text-xs">Qty *</Label><Input type="number" value={it.quantity} onChange={(e) => updItem(i, "quantity", e.target.value)} data-testid={`item-qty-${i}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Unit</Label>
                    <Select value={it.unit} onValueChange={(v) => updItem(i, "unit", v)}>
                      <SelectTrigger data-testid={`item-unit-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{meta.units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Rate (₹)</Label><Input type="number" value={it.rate} onChange={(e) => updItem(i, "rate", e.target.value)} data-testid={`item-rate-${i}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Disc %</Label><Input type="number" value={it.discount_pct} onChange={(e) => updItem(i, "discount_pct", e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">GST %</Label>
                    <Select value={String(it.tax_pct)} onValueChange={(v) => updItem(i, "tax_pct", Number(v))}>
                      <SelectTrigger data-testid={`item-tax-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{meta.gst_rates.map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end justify-end"><span className="text-sm font-medium tabular-nums text-slate-800" data-testid={`item-total-${i}`}>{inr(t.total)}</span></div>
                </div>
                {f.items.length > 1 && <button className="mt-2 flex items-center gap-1 text-xs text-rose-600" onClick={() => removeItem(i)}><Trash2 className="h-3.5 w-3.5" />Remove</button>}
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end">
          <div className="w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between text-slate-600"><span>Taxable value</span><span className="tabular-nums">{inr(totals.taxable)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Tax ({taxMode === "intra" ? "CGST+SGST" : "IGST"})</span><span className="tabular-nums">{inr(totals.tax)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold text-slate-900"><span>Grand total</span><span className="tabular-nums" data-testid="quote-grand-total">{inr(totals.grand)}</span></div>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="mb-4 font-heading font-semibold text-slate-900">Terms & validity</h3>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Validity date</Label><Input type="date" value={f.validity_date} onChange={(e) => upd("validity_date", e.target.value)} data-testid="quote-validity-input" /></div>
            <div className="space-y-1.5"><Label>Terms & conditions</Label><Textarea rows={4} value={f.terms} onChange={(e) => upd("terms", e.target.value)} placeholder="Payment terms, delivery, taxes extra, etc." /></div>
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="mb-4 font-heading font-semibold text-slate-900">Notes</h3>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Customer notes (shown to customer)</Label><Textarea rows={3} value={f.customer_notes} onChange={(e) => upd("customer_notes", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Internal notes (not shared)</Label><Textarea rows={3} value={f.internal_notes} onChange={(e) => upd("internal_notes", e.target.value)} /></div>
          </div>
        </Card>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/quotations")}>Cancel</Button>
        <Button onClick={save} disabled={saving} data-testid="save-quotation-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? "Save changes" : "Create quotation"}
        </Button>
      </div>
    </div>
  );
}
